import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { fetchMenuAccess, saveMenuAccess } from "../../../slice/permission/permissionSlice";
import { fetchAdminMenu } from "../../../slice/admin/adminMenuSlice";
import { ALL_MENU_ITEMS, SECTIONS } from "../Sidebar";

/* ─────────────────────────────────────────────────────────────────────────────
   Menu access, per role.

   The matrix is every screen the sidebar knows (ALL_MENU_ITEMS, grouped the
   way the sidebar groups them) against the three roles. What is stored is one
   row per screen path carrying the roles that may see it, and the page shows
   exactly that: a tick means the role sees the screen in its sidebar.

   This is visibility, not authority. A screen a role cannot see is still
   refused by that screen's own handlers on rank and store scope, so unticking
   "Orders" for an operator tidies their sidebar -- it is not what keeps them
   out. The superadmin column is always on and cannot be edited: the role that
   edits this matrix cannot lock itself out of it, and the server enforces the
   same rule on save.
   ───────────────────────────────────────────────────────────────────────────── */

const SUPERADMIN = 1;
const ROLES = [
  { id: 1, label: "Super Admin", hint: "always on" },
  { id: 2, label: "Store Admin", hint: "runs one store" },
  { id: 3, label: "Operator",    hint: "day-to-day" },
];

/* Every role sees a screen until someone says otherwise; a superAdminOnly
   screen is gated by rank and shows that instead of a choice. */
const defaultRoles = (item) => (item.superAdminOnly ? [SUPERADMIN] : ROLES.map((r) => r.id));

const sectionOf = (key) => SECTIONS.find((s) => s.keys.includes(key))?.title || "More";

/* The sidebar's list, in the sidebar's order, with every item placed in its
   section and anything unlisted in "More" so a new screen can never vanish. */
const GROUPS = (() => {
  const titles = [...SECTIONS.map((s) => s.title), "More"];
  return titles
    .map((title) => ({ title, items: ALL_MENU_ITEMS.filter((it) => sectionOf(it.key) === title) }))
    .filter((g) => g.items.length > 0);
})();

const same = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

/* ── styles: the portal's tokens, nothing hardcoded ─────────────────────── */
const S = {
  page:    { maxWidth: 1080, margin: "0 auto", padding: "8px 0 96px", fontFamily: "var(--mly-font)", color: "var(--mly-text)" },
  head:    { display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 18 },
  title:   { margin: 0, fontFamily: "var(--mly-heading, var(--mly-font))", fontSize: 24, fontWeight: 800, letterSpacing: -0.2 },
  sub:     { margin: "6px 0 0", color: "var(--mly-text-muted)", fontSize: 13, maxWidth: 640, lineHeight: 1.5 },
  card:    { background: "var(--mly-surface)", border: "1px solid var(--mly-border)", borderRadius: 14, boxShadow: "var(--mly-shadow-sm)", overflow: "hidden" },
  table:   { width: "100%", borderCollapse: "separate", borderSpacing: 0 },
  th:      { position: "sticky", top: 0, zIndex: 1, background: "var(--mly-surface-2)", borderBottom: "1px solid var(--mly-border)", padding: "12px 16px", fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", color: "var(--mly-text-muted)", textAlign: "left" },
  thRole:  { textAlign: "center", width: 150 },
  section: { background: "var(--mly-surface-3, var(--mly-surface-2))", padding: "8px 16px", fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", color: "var(--mly-text-soft, var(--mly-text-muted))", borderBottom: "1px solid var(--mly-border-soft, var(--mly-border))" },
  td:      { padding: "10px 16px", borderBottom: "1px solid var(--mly-border-soft, var(--mly-border))", verticalAlign: "middle" },
  tdRole:  { textAlign: "center" },
  name:    { fontSize: 14, fontWeight: 600 },
  path:    { fontFamily: "var(--mly-font-mono)", fontSize: 11, color: "var(--mly-text-muted)", marginTop: 2 },
  pill:    { display: "inline-block", fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", padding: "2px 8px", borderRadius: 999, background: "var(--mly-accent-soft)", color: "var(--mly-accent)", marginLeft: 8, verticalAlign: "middle" },
  hint:    { display: "block", fontSize: 10, fontWeight: 500, letterSpacing: 0, textTransform: "none", color: "var(--mly-text-muted)", marginTop: 2 },
  bar:     { position: "sticky", bottom: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 16px", background: "var(--mly-surface)", borderTop: "1px solid var(--mly-border)" },
  count:   { fontSize: 13, color: "var(--mly-text-muted)" },
  countHot:{ fontSize: 13, color: "var(--mly-text)", fontWeight: 600 },
  btnRow:  { display: "flex", gap: 8 },
  btn:     { fontFamily: "inherit", fontSize: 13, fontWeight: 700, padding: "9px 16px", borderRadius: 10, border: "1px solid var(--mly-border)", background: "var(--mly-surface-2)", color: "var(--mly-text)", cursor: "pointer" },
  btnMain: { fontFamily: "inherit", fontSize: 13, fontWeight: 700, padding: "9px 18px", borderRadius: 10, border: "1px solid var(--mly-accent)", background: "var(--mly-accent)", color: "var(--mly-on-accent)", cursor: "pointer" },
  disabled:{ opacity: 0.55, cursor: "not-allowed" },
  note:    { margin: "0 0 14px", padding: "10px 14px", borderRadius: 10, background: "var(--mly-warn-soft)", color: "var(--mly-text)", fontSize: 13, border: "1px solid var(--mly-border)" },
  error:   { margin: "0 0 14px", padding: "10px 14px", borderRadius: 10, background: "var(--mly-danger-soft)", color: "var(--mly-danger)", fontSize: 13 },
  empty:   { padding: 40, textAlign: "center", color: "var(--mly-text-muted)", fontSize: 14 },
};

/* A tick box that reads as one: the accent when on, the border when off, a
   quieter version when it is not the person's to change. */
const Tick = ({ on, locked, onChange, label }) => (
  <label
    title={label}
    style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: 22, height: 22, borderRadius: 7,
      border: `1.5px solid ${on ? "var(--mly-accent)" : "var(--mly-border)"}`,
      background: on ? "var(--mly-accent)" : "var(--mly-surface)",
      color: "var(--mly-on-accent)", cursor: locked ? "not-allowed" : "pointer",
      opacity: locked ? 0.55 : 1, transition: "background .12s, border-color .12s",
    }}
  >
    <input
      type="checkbox"
      checked={on}
      disabled={locked}
      onChange={(e) => onChange?.(e.target.checked)}
      aria-label={label}
      style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
    />
    {on && (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.7 5.3a1.3 1.3 0 0 1 0 1.9L10.2 17.7a1.3 1.3 0 0 1-1.9 0l-5-5a1.3 1.3 0 0 1 1.9-1.9l4.1 4.1 9.5-9.6a1.3 1.3 0 0 1 1.9 0Z" /></svg>
    )}
  </label>
);

const Permission = () => {
  const dispatch = useDispatch();
  const myRole = useSelector((state) => state.adminUser.role);
  const { rows, loading, saving, error } = useSelector((state) => state.permission);
  const canEdit = Number(myRole) === SUPERADMIN;

  useEffect(() => {
    dispatch(fetchMenuAccess());
  }, [dispatch]);

  /* What the server holds, per path: the saved row's roles, or the default
     for a screen nobody has saved yet. */
  const saved = useMemo(() => {
    const byPath = new Map((rows || []).map((r) => [r.path, (r.roles || []).map(Number)]));
    const m = {};
    ALL_MENU_ITEMS.forEach((it) => { m[it.path] = byPath.get(it.path) || defaultRoles(it); });
    return m;
  }, [rows]);

  /* What the person has ticked. Reset to the saved matrix whenever it arrives. */
  const [local, setLocal] = useState({});
  useEffect(() => { setLocal(saved); }, [saved]);

  const dirtyPaths = useMemo(
    () => ALL_MENU_ITEMS.filter((it) => local[it.path] && !same(local[it.path], saved[it.path])).map((it) => it.path),
    [local, saved]
  );

  const has = (item, role) => (local[item.path] || []).includes(role);
  const set = (item, role, on) =>
    setLocal((prev) => {
      const cur = prev[item.path] || [];
      const next = on ? [...new Set([...cur, role])] : cur.filter((r) => r !== role);
      return { ...prev, [item.path]: next };
    });

  /* A whole column at once, for the "give the operator the lot" moment. */
  const columnState = (role) => {
    const editable = ALL_MENU_ITEMS.filter((it) => !it.superAdminOnly);
    const n = editable.filter((it) => has(it, role)).length;
    return n === 0 ? "none" : n === editable.length ? "all" : "some";
  };
  const setColumn = (role, on) =>
    setLocal((prev) => {
      const next = { ...prev };
      ALL_MENU_ITEMS.forEach((it) => {
        if (it.superAdminOnly) return;
        const cur = next[it.path] || [];
        next[it.path] = on ? [...new Set([...cur, role])] : cur.filter((r) => r !== role);
      });
      return next;
    });

  const onSave = async () => {
    const items = ALL_MENU_ITEMS.map((it, i) => ({
      title: it.title,
      path: it.path,
      icon: it.icon || "",
      sortOrder: i,
      category: sectionOf(it.key),
      roles: local[it.path] || defaultRoles(it),
    }));
    try {
      await dispatch(saveMenuAccess(items)).unwrap();
      /* the sidebar reads its own copy; refresh it so the change shows now */
      dispatch(fetchAdminMenu());
      toast.success("Menu access saved");
    } catch (e) {
      toast.error(e || "Failed to save menu access");
    }
  };

  const onReset = () => setLocal(saved);

  return (
    <div style={S.page}>
      <div style={S.head}>
        <div>
          <h1 style={S.title}>Manage Permissions</h1>
          <p style={S.sub}>
            Choose which screens each role sees in its sidebar. A superadmin always
            sees everything. Hiding a screen tidies a role's menu; the screen itself
            still checks who is calling.
          </p>
        </div>
      </div>

      {!canEdit && (
        <div style={S.note}>
          You can see how access is set, but only a superadmin can change it.
        </div>
      )}
      {error && <div style={S.error}>{error}</div>}

      <div style={S.card}>
        {loading && rows.length === 0 ? (
          <div style={S.empty}>Loading menu access…</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Screen</th>
                  {ROLES.map((r) => {
                    const state = r.id === SUPERADMIN ? "all" : columnState(r.id);
                    return (
                      <th key={r.id} style={{ ...S.th, ...S.thRole }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                          <span>{r.label}<span style={S.hint}>{r.hint}</span></span>
                          <Tick
                            on={state === "all"}
                            locked={!canEdit || r.id === SUPERADMIN}
                            onChange={(on) => setColumn(r.id, on)}
                            label={state === "all" ? `Hide every screen from ${r.label}` : `Show every screen to ${r.label}`}
                          />
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {GROUPS.map((g) => (
                  <React.Fragment key={g.title}>
                    <tr>
                      <td colSpan={1 + ROLES.length} style={S.section}>{g.title}</td>
                    </tr>
                    {g.items.map((it) => {
                      const changed = dirtyPaths.includes(it.path);
                      return (
                        <tr key={it.key} style={changed ? { background: "var(--mly-accent-soft)" } : undefined}>
                          <td style={S.td}>
                            <div style={S.name}>
                              {it.title}
                              {it.superAdminOnly && <span style={S.pill}>Superadmin only</span>}
                            </div>
                            <div style={S.path}>{it.path}</div>
                          </td>
                          {ROLES.map((r) => {
                            const locked = !canEdit || r.id === SUPERADMIN || it.superAdminOnly;
                            const on = r.id === SUPERADMIN ? true : has(it, r.id);
                            return (
                              <td key={r.id} style={{ ...S.td, ...S.tdRole }}>
                                <Tick
                                  on={on}
                                  locked={locked}
                                  onChange={(v) => set(it, r.id, v)}
                                  label={`${it.title} for ${r.label}`}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={S.bar}>
          <span style={dirtyPaths.length ? S.countHot : S.count}>
            {dirtyPaths.length === 0
              ? "No unsaved changes"
              : `${dirtyPaths.length} screen${dirtyPaths.length === 1 ? "" : "s"} changed`}
          </span>
          <div style={S.btnRow}>
            <button
              type="button"
              style={{ ...S.btn, ...(dirtyPaths.length === 0 || saving ? S.disabled : null) }}
              disabled={dirtyPaths.length === 0 || saving}
              onClick={onReset}
            >
              Reset
            </button>
            <button
              type="button"
              style={{ ...S.btnMain, ...(!canEdit || dirtyPaths.length === 0 || saving ? S.disabled : null) }}
              disabled={!canEdit || dirtyPaths.length === 0 || saving}
              onClick={onSave}
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Permission;
